import {Injectable} from '@angular/core';
import {
  Appointment,
  AppointmentInfo,
  AppointmentVehicleInfo,
  Brand,
  CustomerId,
  CustomerReference,
  DistanceUnit,
  Employee,
  Mileage,
  OrderInfo,
  OrderState,
  VehicleInfo
} from '@service-and-repairs/dms-api';
import {SearchServiceOrderSummary} from '../models/search-service-order';
import {VehicleSearchResult as AwpVehicleSearchResult} from '../models/vehicle/search-vehicles-response-body';

@Injectable({providedIn: 'root'})
export class AwpDmsInterfaceModelMapper {
  mapVehicleResults(vehicles: Pick<AwpVehicleSearchResult, 'brand' | 'vin' | 'modelDesignation' | 'numberPlate'>[]): VehicleInfo[] {
    return vehicles.map(vehicle => this.mapVehicle(vehicle));
  }

  mapOrderSummariesToOrderInfos(summaries: SearchServiceOrderSummary[]): OrderInfo[] {
    return summaries
      .map(summary => this.mapSummaryToOrderInfo(summary))
      .filter((order): order is OrderInfo => !!order);
  }

  mapOrderSummariesToAppointmentInfos(summaries: SearchServiceOrderSummary[]): AppointmentInfo[] {
    return summaries
      .map(summary => this.mapSummaryToAppointmentInfo(summary))
      .filter((appointment): appointment is AppointmentInfo => !!appointment);
  }

  private mapSummaryToOrderInfo(summary: SearchServiceOrderSummary): OrderInfo | null {
    if (!summary?.dmsServiceOrderId) {
      return null;
    }
    const vehicle = this.mapOrderVehicle(summary);
    const mileage = this.mapMileage(summary.mileage);
    const appointment = this.mapAppointment(summary.vehicleReceptionData);
    const serviceAdvisor = this.mapEmployee(summary.vehicleReceptionData?.serviceAdvisor);
    const returnAdvisor = this.mapEmployee(summary.vehicleReturnData?.serviceAdvisor);
    const customer = this.mapCustomer(summary.customerReference);
    return new OrderInfo(
      summary.dmsServiceOrderId,
      this.mapOrderState(summary.serviceOrderState),
      summary.dmsServiceOrderState ?? null,
      summary.creationDateTime ? new Date(summary.creationDateTime) : null,
      summary.lastChangedDateTime ? new Date(summary.lastChangedDateTime) : null,
      appointment,
      serviceAdvisor,
      returnAdvisor,
      vehicle,
      mileage,
      customer
    );
  }

  private mapSummaryToAppointmentInfo(summary: SearchServiceOrderSummary): AppointmentInfo | null {
    if (!summary?.dmsServiceOrderId || !summary.vehicleReference) {
      return null;
    }
    const appointmentVehicle = new AppointmentVehicleInfo(
      summary.vehicleReference.vin ?? null,
      summary.vehicleReference.numberPlate ?? null,
      summary.dmsServiceOrderId,
      summary.vehicleReference.modelDesignation ?? null
    );
    const serviceAdvisor = this.mapEmployee(summary.vehicleReceptionData?.serviceAdvisor);
    const customer = this.mapCustomer(summary.customerReference);
    return new AppointmentInfo(
      summary.dmsServiceOrderId,
      summary.creationDateTime ? new Date(summary.creationDateTime) : null,
      summary.lastChangedDateTime ? new Date(summary.lastChangedDateTime) : null,
      summary.vehicleReceptionData?.startDateTime ? new Date(summary.vehicleReceptionData.startDateTime) : null,
      serviceAdvisor,
      appointmentVehicle,
      customer
    );
  }

  private mapOrderVehicle(summary: SearchServiceOrderSummary): VehicleInfo {
    const vehicleReference = summary.vehicleReference;
    if (!vehicleReference) {
      return new VehicleInfo(null, null, null, null, null, null, null);
    }
    return this.mapVehicle({
      brand: vehicleReference.brand,
      vin: vehicleReference.vin,
      modelDesignation: vehicleReference.modelDesignation,
      numberPlate: vehicleReference.numberPlate
    });
  }

  private mapVehicle(vehicle: Pick<AwpVehicleSearchResult, 'brand' | 'vin' | 'modelDesignation' | 'numberPlate'>): VehicleInfo {
    return new VehicleInfo(
      vehicle.vin ?? null,
      null,
      this.mapVehicleBrand(vehicle.brand),
      null,
      vehicle.modelDesignation ?? null,
      null,
      vehicle.numberPlate ?? null
    );
  }

  private mapOrderState(state?: string): OrderState {
    switch (state) {
      case 'ConfirmedByCustomer':
        return OrderState.ConfirmedByCustomer;
      case 'WorkInProgress':
        return OrderState.WorkInProgress;
      case 'WorkshopFinished':
        return OrderState.WorkshopFinished;
      case 'OrderCancelled':
        return OrderState.OrderCancelled;
      case 'OrderInvoiced':
        return OrderState.OrderInvoiced;
      default:
        return OrderState.WorkInProgress;
    }
  }

  private mapVehicleBrand(brand?: string): Brand | null {
    switch (brand) {
      case 'BMW':
        return Brand.Bmw;
      case 'BMW_I':
        return Brand.Bmwi;
      case 'MINI':
        return Brand.Mini;
      case 'MOT':
        return Brand.BmwMotorcycle;
      case 'RR':
        return Brand.RollyRoyce;
      case 'ZINORO':
        return Brand.Zinoro;
      default:
        return Brand.Bmw;
    }
  }

  private mapMileage(mileage?: { unit?: string; value?: number; }): Mileage | null {
    if (mileage?.value == null) {
      return null;
    }
    const unit = mileage.unit === 'miles' ? DistanceUnit.MILES : DistanceUnit.KILOMETERS;
    return new Mileage(mileage.value, unit);
  }

  private mapAppointment(receptionData?: { startDateTime?: string; endDateTime?: string; }): Appointment | null {
    if (!receptionData?.startDateTime) {
      return null;
    }
    const start = new Date(receptionData.startDateTime);
    const end = receptionData.endDateTime ? new Date(receptionData.endDateTime) : null;
    return new Appointment(start, end);
  }

  private mapEmployee(advisor?: { dmsId?: string; firstName?: string; name?: string; }): Employee | null {
    if (!advisor) {
      return null;
    }
    const name = advisor.firstName || advisor.name || null;
    return new Employee(name, advisor.dmsId ?? null);
  }

  private mapCustomer(reference?: {
    dmsCustomerId: string;
    gcdmCustomerId?: string;
    firstName?: string;
    lastName?: string;
  }): CustomerReference | null {
    if (!reference?.dmsCustomerId) {
      return null;
    }
    const customerId = new CustomerId(reference.dmsCustomerId, null, null, null, reference.gcdmCustomerId ?? null);
    // FIXME: If only first name is present, the result will have the first name duplicated in first and last name.
    //  first: 'Peter', last: undefined ==> full name: 'Peter Peter')
    return new CustomerReference(customerId, reference.firstName ?? null, reference.lastName ?? reference.firstName ?? '');
  }
}
